import { Injectable } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import moment from 'moment-timezone';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import _ from 'lodash-es';
import { Notification } from './notifications-overlay.types';
import { RoutingUtils } from '@herrenknecht-connected/angular-shared-utils-core';
import { ComponentRoute, RouteName, RouteNames } from './app-frame.types';

export const MOBILE_AQUISITION_MODE_QUERY_PARAM = 'MobileAquisition';

@Injectable({
  providedIn: 'root',
})
export class AppFrameService {
  private _sidenavVisibleSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _sidenavMobileOverlayEnabled: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private _sidenavOpenSubject = new BehaviorSubject<boolean>(true);
  private _topbarSpecificHelpRequested: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private _notifications = [] as Notification[];
  private _notificationSubject = new BehaviorSubject<Notification[]>([] as Notification[]);
  private _componentReplySubject = new ReplaySubject<ComponentRoute | undefined>(1);
  private _addonRoutes?: RouteNames;

  constructor(
    private _logger: NGXLogger,
    private _router: Router,
  ) {}

  get connectedEnvironment() {
    return RoutingUtils.idmServicesEnvironment;
  }

  private _currentComponent?: ComponentRoute;

  get currentComponent(): ComponentRoute | undefined {
    return this._currentComponent;
  }

  get notifications$() {
    return this._notificationSubject.asObservable();
  }

  get unseenNotifications$() {
    return this._notificationSubject.asObservable().pipe(map((notifications) => notifications.filter((n) => !n._seen)));
  }

  get seenNotifications$() {
    return this._notificationSubject.asObservable().pipe(map((notifications) => notifications.filter((n) => n._seen)));
  }

  get sidenavOpen$() {
    return this._sidenavOpenSubject.asObservable();
  }

  get sidenavVisible$() {
    return this._sidenavVisibleSubject.asObservable();
  }

  // noinspection JSUnusedGlobalSymbols => Used in HTML-Template
  get sidenavMobileOverlayEnabled$() {
    return this._sidenavMobileOverlayEnabled.asObservable();
  }

  get _topbarSpecificHelpRequested$() {
    return this._topbarSpecificHelpRequested.asObservable();
  }

  initialize(addonRoutes: RouteNames) {
    this._addonRoutes = addonRoutes;
  }

  getActiveComponent(url: string): ComponentRoute {
    const relativeUrl = url.replace(RoutingUtils.applicationBaseHref, '/');
    const urlMatch = relativeUrl.match(/^\/(?<addon>[\w-]+)(\/(?<app>[\w-]+))?.*$/);

    const addon = urlMatch?.groups?.['addon'] as RouteName;
    const isUndefinedAddon = !_.values(this._addonRoutes).includes(addon);

    const result = { addon, app: urlMatch?.groups?.['app'] as RouteName, isUndefinedAddon };

    // try to get app from page parameter for iframe components
    if (!result.app) {
      result.app = (new URLSearchParams(relativeUrl.split('?')[1]).get('page') as RouteName) ?? undefined;
    }

    return result;
  }

  /** Long lived observable that triggers on route changes. */
  onComponentChange$(): Observable<ComponentRoute | undefined> {
    return this._componentReplySubject.asObservable();
  }

  updateNotification(notification: Notification, upsert = false) {
    const id = _.findIndex(this._notifications, (n) => n.uuid === notification.uuid);
    notification._seen = false;
    notification._updatedAt = moment();
    if (id < 0) {
      if (!upsert) {
        this._logger.error(`Could not replace notification ${notification.uuid} because it does not exist.`);
        return;
      }
      // add new element
      this._notifications.push(notification);
    } else {
      this._notifications[id] = notification;
    }
    this._notificationSubject.next(this._notifications);
  }

  markNotificationsAsSeen() {
    for (const n of this._notifications) {
      n._seen = true;
    }
    this._notificationSubject.next(this._notifications);
  }

  removeNotification(uuid: string) {
    this._notifications = this._notifications.filter((n) => n.uuid !== uuid);
    this._notificationSubject.next(this._notifications);
  }

  showSplashScreen(visible: boolean) {
    if (visible) return window.initializationScreen?.show();
    return window.initializationScreen?.hide();
  }

  setSplashScreenText(title: string, text: string) {
    const separator = title ? '<br><br>' : '';
    return window.initializationScreen?.text(`${title}${separator}${text}`);
  }

  showSidenav(visible: boolean) {
    this.fixObservableExpressionChangedAfterChecked(() => this._sidenavVisibleSubject.next(visible));
  }

  showSidenavMobileOverlay(visible: boolean) {
    this.fixObservableExpressionChangedAfterChecked(() => this._sidenavMobileOverlayEnabled.next(visible));
  }

  openSidenav() {
    this.fixObservableExpressionChangedAfterChecked(() => this._sidenavOpenSubject.next(true));
  }

  closeSidenav() {
    this.fixObservableExpressionChangedAfterChecked(() => this._sidenavOpenSubject.next(false));
  }

  toggleSidenav() {
    this.fixObservableExpressionChangedAfterChecked(() => this._sidenavOpenSubject.next(!this._sidenavOpenSubject.value));
  }

  setSpecificHelpRequested(isRequested: boolean) {
    this.fixObservableExpressionChangedAfterChecked(() => this._topbarSpecificHelpRequested.next(isRequested));
  }

  /** Initializes the app frame service BEFORE the actual application start. Should ONLY be called from an app initializer */
  init() {
    this._router.events
      .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((navigationEnd: NavigationEnd) => {
        // remove / and query params from url
        this._currentComponent = this.getActiveComponent(navigationEnd.urlAfterRedirects);
        this._componentReplySubject.next(this._currentComponent);
      });
  }

  private fixObservableExpressionChangedAfterChecked(function_: () => void) {
    setTimeout(function_, 0);
  }
}
